import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText,
  Shield,
  AlertTriangle,
  Activity,
  Plus,
  ChevronRight,
  Search,
  Check
} from 'lucide-react';

const CATEGORY_ICONS = {
  fraude: Shield,
  compliance: AlertTriangle,
  operativo: Activity
};

const CATEGORY_COLORS = {
  fraude: { bg: '#fef2f2', border: '#fecaca', text: '#dc2626' },
  compliance: { bg: '#fffbeb', border: '#fde68a', text: '#d97706' },
  operativo: { bg: '#eff6ff', border: '#bfdbfe', text: '#2563eb' }
};

const SEVERITY_COLORS = {
  CRITICA: '#ef4444',
  ALTA: '#f97316',
  MEDIA: '#eab308',
  BAJA: '#22c55e'
};

export const MonitorTemplates = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customName, setCustomName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesRes, categoriesRes] = await Promise.all([
        api.get('/monitor-templates/'),
        api.get('/monitor-templates/categories')
      ]);
      
      setTemplates(templatesRes.data);
      setCategories(categoriesRes.data.categorias);
    } catch (error) {
      console.error('Error cargando plantillas:', error);
      toast.error('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMonitor = async () => {
    if (!selectedTemplate) return;
    
    setCreating(true);
    try {
      const params = customName ? `?nombre_personalizado=${encodeURIComponent(customName)}` : '';
      const response = await api.post(`/monitor-templates/${selectedTemplate.id}/create-monitor${params}`);
      
      toast.success('Monitor creado exitosamente');
      setShowCreateDialog(false);
      setSelectedTemplate(null);
      setCustomName('');
      
      // Navegar a la página de monitores
      navigate('/monitors');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear monitor');
    } finally {
      setCreating(false);
    }
  };

  const openCreateDialog = (template) => {
    setSelectedTemplate(template);
    setCustomName(template.nombre);
    setShowCreateDialog(true);
  };

  // Filtrar plantillas
  const filteredTemplates = templates.filter(t => {
    const matchCategory = filterCategory === 'all' || t.categoria === filterCategory;
    const matchSearch = t.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       t.descripcion.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCategory && matchSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando plantillas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50" data-testid="monitor-templates-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">
            Plantillas de Monitores
          </h1>
          <p className="text-slate-600 mt-1">
            Monitores pre-configurados para casos comunes
          </p>
        </div>
      </div>

      {/* Categorías */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Button
          variant={filterCategory === 'all' ? 'default' : 'outline'}
          onClick={() => setFilterCategory('all')}
          style={filterCategory === 'all' 
            ? { backgroundColor: '#2563eb', color: '#ffffff' }
            : { borderColor: '#cbd5e1', color: '#374151' }
          }
        >
          <FileText size={16} className="mr-2" />
          Todas ({templates.length})
        </Button>
        {categories.map(cat => {
          const Icon = CATEGORY_ICONS[cat.id] || FileText;
          const colors = CATEGORY_COLORS[cat.id] || CATEGORY_COLORS.operativo;
          const isActive = filterCategory === cat.id;
          
          return (
            <Button
              key={cat.id}
              variant="outline"
              onClick={() => setFilterCategory(cat.id)}
              style={isActive 
                ? { backgroundColor: colors.bg, borderColor: colors.text, color: colors.text }
                : { borderColor: '#cbd5e1', color: '#374151' }
              }
            >
              <Icon size={16} className="mr-2" />
              {cat.nombre} ({cat.count})
            </Button>
          );
        })}
      </div>

      {/* Búsqueda */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar plantillas..."
          className="pl-10 max-w-md"
          style={{ backgroundColor: '#ffffff' }}
        />
      </div>

      {/* Grid de plantillas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredTemplates.map(template => {
          const Icon = CATEGORY_ICONS[template.categoria] || FileText;
          const colors = CATEGORY_COLORS[template.categoria] || CATEGORY_COLORS.operativo;
          
          return (
            <Card 
              key={template.id}
              className="p-5 bg-white border-slate-200 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div 
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: colors.bg }}
                >
                  <Icon size={24} style={{ color: colors.text }} />
                </div>
                <span 
                  className="px-2 py-1 rounded text-xs font-medium text-white"
                  style={{ backgroundColor: SEVERITY_COLORS[template.severidad_sugerida] }}
                >
                  {template.severidad_sugerida}
                </span>
              </div>
              
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                {template.nombre}
              </h3>
              
              <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                {template.descripcion}
              </p>
              
              <div className="flex flex-wrap gap-2 mb-4">
                <span 
                  className="px-2 py-1 rounded text-xs font-medium"
                  style={{ backgroundColor: colors.bg, color: colors.text }}
                >
                  {template.categoria}
                </span>
                <span className="px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-600">
                  {template.tipo_monitor}
                </span>
                {template.ventana_temporal && (
                  <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700">
                    {template.ventana_temporal}
                  </span>
                )}
              </div>
              
              <Button
                onClick={() => openCreateDialog(template)}
                className="w-full"
                style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
                data-testid={`use-template-${template.id}`}
              >
                <Plus size={16} className="mr-2" />
                Usar Plantilla
              </Button>
            </Card>
          );
        })}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <FileText size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No se encontraron plantillas</p>
        </div>
      )}

      {/* Dialog Crear Monitor */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="text-slate-900">Crear Monitor desde Plantilla</DialogTitle>
            <DialogDescription className="text-slate-600">
              {selectedTemplate?.nombre}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg bg-slate-50">
                <p className="text-sm text-slate-600 mb-3">{selectedTemplate.descripcion}</p>
                <div className="flex flex-wrap gap-2">
                  <span 
                    className="px-2 py-1 rounded text-xs font-medium text-white"
                    style={{ backgroundColor: SEVERITY_COLORS[selectedTemplate.severidad_sugerida] }}
                  >
                    Severidad: {selectedTemplate.severidad_sugerida}
                  </span>
                  <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                    Tipo: {selectedTemplate.tipo_monitor}
                  </span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Nombre del Monitor
                </label>
                <Input
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="Nombre personalizado..."
                  style={{ backgroundColor: '#ffffff' }}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Puedes personalizar el nombre o dejar el predeterminado
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowCreateDialog(false)}
              style={{ color: '#374151' }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateMonitor}
              disabled={creating}
              style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
            >
              {creating ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creando...
                </span>
              ) : (
                <span className="flex items-center">
                  <Check size={16} className="mr-2" />
                  Crear Monitor
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MonitorTemplates;
